// Fachsprache und Aufrufe der Beziehungen zwischen zwei Kontakten (ST-REL-001;
// CM-REL-001..003; ADR-0061).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in
// context-roles.ts und classifications.ts.
//
// DIE WOERTER "PARTY", "RELATIONSHIP", "TYPE CODE", "SIDE", "A" UND "B"
// KOMMEN AUF DEM BILDSCHIRM NICHT VOR (NFR-UX-003, CM-UX-010). Die Nutzerin
// waehlt eine LESART in Worten -- "arbeitet für", "beschäftigt" -- und nie
// eine Richtung (ADR-0061 Ziffer 4). Die Lesarten sind Katalogtext und kommen
// vom Server; diese Datei erfindet keine. Eine Probe (relationships.test.ts)
// haelt jeden Satz dieser Datei gegen eine Verbotsliste.
//
// DER WIEDERHOLSCHUTZ liegt in client-request.ts (ADR-0060); diese Datei
// nimmt die Kennung nur entgegen und schickt sie mit.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import { onlyRequestIdRejected } from './client-request.ts';
import { sendCommand, type CommandMessages } from './command-client.ts';
import { PRODUKT_ZEITZONE, type PartySummary } from './parties.ts';

export type SendLike = (input: string, init?: RequestInit) => Promise<Response>;

export type ContactSide = 'a' | 'b';
export type CounterpartKind = 'person' | 'organization';

/** Eine aktive Beziehung aus Sicht des geoeffneten Kontakts -- die acht Felder der Leseroute. */
export interface RelationshipView {
  readonly relationshipId: string;
  readonly relationshipTypeCode: string;
  /** Die Lesart aus Sicht des geoeffneten Kontakts, etwa "arbeitet für". */
  readonly reading: string;
  readonly counterpartPartyId: string;
  readonly counterpartDisplayName: string;
  readonly validFrom: string;
  /** Der Stand fuer das Beenden; ein abweichender Stand ist ein 409. */
  readonly lockVersion: number;
  /** Die Funktion als Code der Werteliste (Migration 0048) oder null; die Bezeichnung kommt mit der Anzeige. */
  readonly roleCode: string | null;
}

/** Eine waehlbare Lesart fuer eine neue Beziehung (ADR-0061 Ziffer 4). */
export interface RelationshipOption {
  readonly relationshipTypeCode: string;
  readonly contactSide: ContactSide;
  readonly reading: string;
  /** Die erlaubte Art der Gegenpartei; null heisst beliebig. */
  readonly counterpartKind: CounterpartKind | null;
}

export type RelationshipsResult =
  | {
      readonly outcome: 'loaded';
      readonly relationships: readonly RelationshipView[];
      readonly hasMore: boolean;
      /** Der Cursor der naechsten Seite (fetchRelationshipPage); fehlt er bei hasMore, bleibt nur der Hinweis. */
      readonly nextCursor?: string;
      readonly options: readonly RelationshipOption[];
    }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

/** Eine Seite Kanten ohne Lesarten -- fuer Leser, die nichts festhalten. */
export type RelationshipPageResult =
  | {
      readonly outcome: 'loaded';
      readonly relationships: readonly RelationshipView[];
      readonly hasMore: boolean;
      /** Wo die naechste Seite ansetzt; fehlt bei einer API vor dem Blaettern. */
      readonly nextCursor: string | undefined;
    }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

export interface FieldProblem {
  readonly fieldPath: string;
  readonly reasonCode: string;
}

export type CreateRelationshipResult =
  | { readonly outcome: 'created'; readonly relationshipId: string }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'invalid'; readonly errors: readonly FieldProblem[] }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage; readonly reasonCode?: string | undefined };

export type EndRelationshipResult =
  | {
      readonly outcome: 'ended';
      /** Das Ende stammt vom abgebrochenen ersten Versuch; die Wiederholung hat es nur bestaetigt. */
      readonly confirmedAfterAbort?: true;
    }
  | { readonly outcome: 'not-found' }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage; readonly reasonCode?: string | undefined };

// --- Die Woerter -------------------------------------------------------------

/**
 * Der innere Schluessel einer Lesart -- fuer die Auswahl, NIE fuer den
 * Bildschirm. Ein Typ traegt bei einer Organisation bis zu zwei Lesarten
 * (ADR-0061 Ziffer 4), erst Typ UND Seite machen sie eindeutig.
 */
export function optionKey(option: Pick<RelationshipOption, 'relationshipTypeCode' | 'contactSide'>): string {
  return `${option.relationshipTypeCode}:${option.contactSide}`;
}

/** Die Zeile: "arbeitet für Verlag Nord". */
export function relationshipLine(relationship: RelationshipView): string {
  return `${relationship.reading} ${relationship.counterpartDisplayName}`;
}

export function sinceText(formattedSince: string): string {
  return `seit ${formattedSince}`;
}

/**
 * "03/2024" wie v4: fuer "seit" in der Ansprechpartner-Zeile genuegt der Monat.
 * In der Produktzeitzone, sonst stuende westlich von UTC eine Beziehung vom
 * Monatsersten einen Monat zu frueh da.
 */
export function formatSinceMonth(iso: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat('de-DE', { month: '2-digit', year: 'numeric', timeZone: PRODUKT_ZEITZONE }).format(datum);
}

/** Der zugaengliche Name von "Beenden": der volle Satz, nicht nur das Verb. */
export function endButtonLabel(relationship: RelationshipView): string {
  return `Beziehung „${relationshipLine(relationship)}" beenden`;
}

/**
 * "Erneut versuchen" nach einem abgebrochenen Beenden: der Name nennt die
 * Zeile, denn die Statusregion steht unter der ganzen Liste. Er beginnt mit
 * dem sichtbaren Text (WCAG 2.5.3).
 */
export function endRetryLabel(relationship: RelationshipView): string {
  return `Erneut versuchen: ${endButtonLabel(relationship)}`;
}

/** Der Leerzustand in Worten (5.2 Nr. 9) -- keine leere Liste ohne Satz. */
export const EMPTY_STATEMENT = 'Für diesen Kontakt ist noch keine Beziehung zu einem anderen Kontakt festgehalten.';

/**
 * Der ehrliche Hinweis bei mehr als 50 (ADR-0061 Ziffer 14): OHNE Zahl, denn
 * der Server zaehlt bewusst nicht (TR-017). Er sagt, was fehlt und dass es
 * gespeichert ist.
 */
export const HAS_MORE_NOTICE =
  'Angezeigt werden die ersten 50. Weitere Beziehungen dieses Kontakts sind gespeichert, erscheinen hier aber noch nicht.';

/** Die Art der Gegenpartei in Worten, fuer den Satz unter der Trefferliste. */
export function counterpartKindPhrase(kind: CounterpartKind | null): string {
  if (kind === 'person') return 'eine Person';
  if (kind === 'organization') return 'eine Organisation';
  return 'einen Kontakt';
}

/**
 * Die Bestaetigung NACH dem Festhalten. Sie sagt, dass die Beziehung auch am
 * anderen Kontakt steht -- einmal gespeichert, von beiden Seiten lesbar
 * (CM-REL-002).
 */
export function createdAnnouncement(reading: string, counterpartDisplayName: string): string {
  return `Festgehalten: „${reading} ${counterpartDisplayName}". Die Beziehung steht auch bei ${counterpartDisplayName}, dort aus dessen Sicht.`;
}

/**
 * Die Bestaetigung NACH dem Beenden (NFR-UX-005, ADR-0061 Ziffer 17):
 * ausdruecklich "beendet, nicht gelöscht", und der Rueckweg steht im Satz.
 * Er sagt, was der Rueckweg NICHT zurueckholt: eine neu festgehaltene
 * Beziehung beginnt heute (den Beginn setzt LIAISON, ADR-0061 Ziffer 10), und
 * eine Funktion wird neu gewaehlt. `confirmedAfterAbort`: das Ende stammt vom
 * abgebrochenen ersten Versuch -- der Satz sagt das, statt so zu tun, als
 * haette die Wiederholung beendet.
 */
export function endedAnnouncement(relationship: RelationshipView, confirmedAfterAbort = false): string {
  const zeile = `„${relationshipLine(relationship)}"`;
  const ende = confirmedAfterAbort ? `Dein erster Versuch hat ${zeile} schon beendet, nicht gelöscht.` : `${zeile} ist beendet, nicht gelöscht.`;
  const funktion = relationship.roleCode === null ? '' : ' Die Funktion wählst du dabei neu.';
  return `${ende} War das ein Versehen? Dann halte sie über „Beziehung festhalten" neu fest. Sie beginnt dann heute.${funktion}`;
}

/** Die Ueberschrift der Erfolgsmeldung nach jedem Beenden. */
export const ENDED_HEADLINE = 'Die Beziehung ist beendet.';

/**
 * Der Ausgang einer Wiederholung NACH einem Transportabbruch (Auflage des
 * Dirigenten, 29.09.2026): meldet der Server "bereits beendet", hat der
 * abgebrochene erste Versuch das Ende geschrieben -- das ist ein Erfolg und
 * kein Hinweis. Ohne vorangegangenen Abbruch bleibt es der Hinweis
 * (ALREADY_ENDED_MESSAGE): dann hat jemand anderes beendet.
 */
export function settleEndAfterAbort(result: EndRelationshipResult, afterAbort: boolean): EndRelationshipResult {
  if (afterAbort && result.outcome === 'rejected' && result.reasonCode === 'relationship_already_ended') {
    return { outcome: 'ended', confirmedAfterAbort: true };
  }
  return result;
}

/** Der Satz unter der Trefferliste, wenn die Suche nichts Passendes fand. */
export function noEligibleHitsStatement(totalHits: number, kind: CounterpartKind | null): string {
  return totalHits === 0
    ? 'Kein Kontakt passt zu diesem Suchbegriff. Prüfe die Schreibweise oder suche nach einem anderen Teil des Namens.'
    : `Unter den Treffern ist keiner, der passt: diese Beziehung braucht ${counterpartKindPhrase(kind)}, und dieser Kontakt selbst steht nicht zur Wahl.`;
}

/** Der Satz nach einer Suche mit passenden Treffern -- fuer die Statusregion. */
export function eligibleHitsStatement(count: number, hasMore: boolean): string {
  const satz = count === 1 ? 'Ein passender Kontakt gefunden.' : `${count} passende Kontakte gefunden.`;
  return hasMore ? `${satz} Es gibt weitere Treffer; gib mehr vom Namen ein, um genauer zu suchen.` : satz;
}

/**
 * Die Treffer, die als Gegenpartei in Frage kommen: die erlaubte Art der
 * gewaehlten Lesart und NIE der geoeffnete Kontakt selbst (der Server lehnt
 * beides ebenfalls ab; die Oberflaeche ist die freundlichere Kontrolle).
 */
export function eligibleCounterparts(
  hits: readonly PartySummary[],
  option: Pick<RelationshipOption, 'counterpartKind'> | undefined,
  ownPartyId: string,
): readonly PartySummary[] {
  return hits.filter(
    (hit) => hit.partyId !== ownPartyId && (option === undefined || option.counterpartKind === null || hit.kind === option.counterpartKind),
  );
}

// --- Die Saetze: Problem, Wirkung, naechster Schritt (NFR-UX-005) ------------

export const DUPLICATE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Beziehung ist bereits festgehalten.',
  explanation: 'Die beiden Kontakte stehen schon so zueinander; ein zweites Mal wird die Beziehung nicht festgehalten.',
  nextStep: 'Die Liste zeigt den gespeicherten Stand.',
  retryable: false,
  // Die Beziehung steht schon so da: ein Hinweis, kein Fehler.
  notice: true,
});

export const PARTY_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'An diesem Kontakt lassen sich keine Beziehungen mehr ändern.',
  explanation: 'Der Kontakt ist zusammengeführt, archiviert oder anonymisiert. Sein Stand ist abgeschlossen.',
  nextStep: 'Öffne den aktuellen Kontakt über die Kontaktliste.',
  retryable: false,
});

export const COUNTERPART_NOT_WRITABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Mit dem anderen Kontakt lässt sich die Beziehung nicht mehr ändern.',
  explanation: 'Der andere Kontakt ist zusammengeführt, archiviert oder anonymisiert. Es wurde nichts geändert.',
  nextStep: 'Suche den aktuellen Kontakt und halte die Beziehung mit ihm fest.',
  retryable: false,
});

/** Der geoeffnete Kontakt selbst ist nicht mehr sichtbar (404 auf einen Befehl). Keine Vermutung ueber eine Berechtigung. */
export const PARTY_GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diesen Kontakt gibt es hier nicht mehr.',
  explanation: 'Er wurde entfernt, oder du kannst ihn nicht mehr öffnen. Es wurde nichts geändert.',
  nextStep: 'Geh zurück zur Liste und suche ihn dort.',
  retryable: false,
});

export const COUNTERPART_GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Den anderen Kontakt gibt es hier nicht mehr.',
  explanation: 'Er wurde entfernt, oder du kannst ihn nicht mehr öffnen. Es wurde nichts festgehalten.',
  nextStep: 'Suche den anderen Kontakt erneut.',
  retryable: false,
});

/**
 * Die Mengenbremse (ADR-0061 Ziffer 7): Anlegen und Beenden zusammen. Der
 * Satz nennt die Grenze ohne die Zahl -- sie ist ein Betriebswert, kein
 * Versprechen.
 */
export const LIMIT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Grenze für diese Stunde ist erreicht.',
  explanation: 'LIAISON begrenzt, wie viele Beziehungen eine Person je Stunde festhält oder beendet. Es wurde nichts geändert.',
  nextStep: 'Versuche es in einer Stunde erneut. Geht es um viele Kontakte auf einmal, wende dich an die LIAISON-Administration.',
  retryable: false,
});

export const CARDINALITY_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Mehr Beziehungen dieser Art sind hier nicht möglich.',
  explanation: 'Dieser Kontakt hat schon so viele Beziehungen dieser Art, wie LIAISON zulässt. Es wurde nichts festgehalten.',
  nextStep: 'Gilt eine der bestehenden nicht mehr, beende sie zuerst und halte die neue dann fest.',
  retryable: false,
});

/** Der erste Versuch kam an, trug aber andere Angaben (ADR-0060). */
export const REQUEST_REUSED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Wiederholung passte nicht zum ersten Versuch.',
  explanation: 'Beim Wiederholen waren die Angaben andere als beim ersten Versuch. Es wurde nichts doppelt festgehalten.',
  nextStep: 'Prüfe die Liste. Fehlt die Beziehung, halte sie neu fest.',
  retryable: false,
});

/** Der erste Versuch kam an; die Beziehung ist seitdem beendet oder geaendert (ADR-0060). */
export const REQUEST_SUPERSEDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Beziehung wurde inzwischen geändert.',
  explanation: 'Der erste Versuch war angekommen. Seitdem ist die Beziehung beendet oder bearbeitet worden.',
  nextStep: 'Die Liste zeigt den aktuellen Stand.',
  retryable: false,
});

/** Ein Konflikt, fuer den diese Oberflaeche keinen eigenen Satz hat. */
export const CONFLICT_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Beziehung wurde nicht festgehalten.',
  explanation: 'Der Stand hat sich geändert, während du ihn bearbeitet hast.',
  nextStep: 'Die Liste ist neu geladen. Prüfe sie und versuche es erneut.',
  retryable: false,
});

/** Die Wiederholung eines schon gespeicherten Endes -- ein Hinweis, kein Fehler. */
export const ALREADY_ENDED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Beziehung ist bereits beendet.',
  explanation: 'Das Ende war schon gespeichert, vielleicht aus einem anderen Fenster.',
  nextStep: 'Die Liste zeigt jetzt den aktuellen Stand.',
  retryable: false,
  notice: true,
});

export const OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Beziehung wurde inzwischen geändert.',
  explanation: 'Jemand hat sie nach dem Laden bearbeitet. Es wurde nichts beendet.',
  nextStep: 'Die Liste ist neu geladen. Prüfe den Stand und versuche es erneut.',
  retryable: false,
});

/** Funktion aendern auf einen veralteten Stand -- nicht der Satz vom Beenden. */
export const FUNCTION_OUTDATED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Beziehung wurde inzwischen geändert.',
  explanation: 'Jemand hat sie nach dem Laden bearbeitet. Die Funktion wurde nicht geändert.',
  nextStep: 'Die Liste ist neu geladen. Prüfe den Stand und versuche es erneut.',
  retryable: false,
});

export const RELATIONSHIP_GONE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Beziehung gibt es hier nicht mehr.',
  explanation: 'Sie ist inzwischen beendet, oder du kannst den anderen Kontakt nicht mehr öffnen. Es wurde nichts geändert.',
  nextStep: 'Die Liste ist neu geladen und zeigt den aktuellen Stand.',
  retryable: false,
});

/** Festhalten ohne Antwort. Die Wiederholung traegt dieselbe Kennung und legt nichts doppelt an (ADR-0060). */
export const CREATE_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Beziehung festgehalten wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Wähle „Erneut versuchen“. Doppelt festgehalten wird dabei nichts. Deine Auswahl bleibt erhalten.',
  retryable: true,
});

/** Beenden ohne Antwort. Ein schon gespeichertes Ende zaehlt beim naechsten Versuch als Erfolg. */
export const END_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Beziehung beendet wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Versuche es erneut. Doppelt beendet wird dabei nichts.',
  retryable: true,
});

export const LOAD_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Beziehungen sind gerade nicht erreichbar.',
  explanation: 'Es kam keine Antwort.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

export const SEARCH_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Suche ist gerade nicht erreichbar.',
  explanation: 'Es kam keine Antwort.',
  nextStep: 'Prüfe deine Verbindung und suche erneut.',
  retryable: true,
});

/** Die Satz-Tabelle der Konflikte beim Festhalten -- JEDER Grundcode mit eigenem Satz. */
const CREATE_CONFLICTS: Readonly<Record<string, PlainMessage>> = Object.freeze({
  party_not_writable: PARTY_NOT_WRITABLE_MESSAGE,
  counterpart_not_writable: COUNTERPART_NOT_WRITABLE_MESSAGE,
  relationship_duplicate: DUPLICATE_MESSAGE,
  relationship_limit_reached: LIMIT_MESSAGE,
  relationship_cardinality_reached: CARDINALITY_MESSAGE,
  client_request_reused: REQUEST_REUSED_MESSAGE,
  client_request_superseded: REQUEST_SUPERSEDED_MESSAGE,
});

/** Die Satz-Tabelle der Konflikte beim Beenden. */
const END_CONFLICTS: Readonly<Record<string, PlainMessage>> = Object.freeze({
  relationship_already_ended: ALREADY_ENDED_MESSAGE,
  relationship_outdated: OUTDATED_MESSAGE,
  relationship_limit_reached: LIMIT_MESSAGE,
  party_not_writable: PARTY_NOT_WRITABLE_MESSAGE,
  counterpart_not_writable: COUNTERPART_NOT_WRITABLE_MESSAGE,
});

/** "Funktion aendern" auf dieselbe Funktion -- ein Hinweis, kein Fehler. */
export const ROLE_UNCHANGED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Diese Funktion ist bereits eingetragen.',
  explanation: 'Die Beziehung trägt schon diese Funktion. Es wurde nichts geändert.',
  nextStep: 'Wähle eine andere Funktion oder lass sie, wie sie ist.',
  retryable: false,
  notice: true,
});

/**
 * Die Satz-Tabelle der Konflikte beim Aendern der Funktion: die des Beendens
 * -- die alte Kante endet --, dazu die unveraenderte Funktion und die
 * Dublette der zweiten Haelfte.
 */
const ROLE_CONFLICTS: Readonly<Record<string, PlainMessage>> = Object.freeze({
  ...END_CONFLICTS,
  relationship_outdated: FUNCTION_OUTDATED_MESSAGE,
  relationship_role_unchanged: ROLE_UNCHANGED_MESSAGE,
  relationship_duplicate: DUPLICATE_MESSAGE,
});

/** Die Grundcodes, die diese Oberflaeche uebersetzt -- fuer die Probe gegen den Schreibweg. */
export const CREATE_CONFLICT_CODES: readonly string[] = Object.freeze(Object.keys(CREATE_CONFLICTS));
export const END_CONFLICT_CODES: readonly string[] = Object.freeze(Object.keys(END_CONFLICTS));
export const ROLE_CONFLICT_CODES: readonly string[] = Object.freeze(Object.keys(ROLE_CONFLICTS));

// --- Die Funktion an der Beziehung (CM-REL-006, Migration 0048) --------------

/**
 * Die dreizehn Funktionen in Worten, in der Reihenfolge der Werteliste. Die
 * Codes stehen in apps/api (ROLE_CODES); relationships.test.ts liest die Datei
 * dort und haelt beide Listen gleich. "management" heisst bewusst
 * "Geschäftsführung und Inhaberschaft": bei Buchhandlungen ist meist die
 * Inhaberin die Ansprechpartnerin (decisions, ST-REL-001).
 */
export const ROLE_LABELS: Readonly<Record<string, string>> = Object.freeze({
  management: 'Geschäftsführung und Inhaberschaft',
  editorial: 'Lektorat',
  press: 'Presse',
  marketing: 'Marketing',
  sales: 'Vertrieb',
  purchasing: 'Einkauf',
  rights_licensing: 'Rechte und Lizenzen',
  events: 'Veranstaltungen',
  production: 'Herstellung',
  accounting: 'Buchhaltung',
  customer_service: 'Kundenservice',
  assistant: 'Assistenz',
  other: 'Sonstiges',
});

export const ROLE_CODES: readonly string[] = Object.freeze(Object.keys(ROLE_LABELS));

/** Die Funktion in Worten; ein unbekannter Code wird nicht als Code gezeigt. */
export function roleLabel(roleCode: string | null): string | undefined {
  return roleCode !== null && Object.hasOwn(ROLE_LABELS, roleCode) ? ROLE_LABELS[roleCode] : undefined;
}

/** Nur an "arbeitet für" und "arbeitet frei für" steht eine Funktion (F1). */
const ROLE_TYPES: readonly string[] = Object.freeze(['works_for', 'contributes_to']);

export function typeCarriesRole(relationshipTypeCode: string): boolean {
  return ROLE_TYPES.includes(relationshipTypeCode);
}

/**
 * Die Kanten zu Menschen, die an einer Organisation zuerst stehen: wer dort
 * arbeitet, frei mitarbeitet oder sie leitet. "vertritt" gehoert nicht dazu:
 * eine vertretene Autorin ist Mandantin der Agentur, keine Ansprechpartnerin
 * (decisions, ST-REL-001).
 */
export const CONTACT_PERSON_TYPES: readonly string[] = Object.freeze(['works_for', 'contributes_to', 'leads']);

/**
 * Ob der geoeffnete Kontakt eine Organisation ist -- abgelesen an den
 * Lesarten, ohne weiteres Feld in der Leseroute (Knotenetat). Diese drei Typen
 * verbinden laut Katalog immer eine Person mit einer Organisation; wird einer
 * aus Sicht der zweiten Seite angeboten, ist der Kontakt die Organisation.
 * Ohne solche Lesart bleibt die Liste ungeteilt.
 */
export function isOrganizationView(options: readonly RelationshipOption[]): boolean {
  return options.some((option) => option.contactSide === 'b' && CONTACT_PERSON_TYPES.includes(option.relationshipTypeCode));
}

export interface RelationshipGroups {
  /** An einer Organisation: die Kanten zu Menschen, unter "Ansprechpersonen". */
  readonly contactPersons: readonly RelationshipView[];
  readonly others: readonly RelationshipView[];
}

/** Teilt die Liste an einer Organisation; die Reihenfolge des Servers bleibt je Gruppe. */
export function groupRelationships(relationships: readonly RelationshipView[], organization: boolean): RelationshipGroups {
  if (!organization) {
    return { contactPersons: [], others: relationships };
  }
  return {
    contactPersons: relationships.filter((eintrag) => CONTACT_PERSON_TYPES.includes(eintrag.relationshipTypeCode)),
    others: relationships.filter((eintrag) => !CONTACT_PERSON_TYPES.includes(eintrag.relationshipTypeCode)),
  };
}

/**
 * Der zugaengliche Name von "Funktion ändern": der volle Satz, wie bei
 * "Beenden" -- und der sichtbare Text steht vorn (WCAG 2.5.3), damit
 * Sprachsteuerung "Funktion ändern" trifft.
 */
export function changeRoleButtonLabel(relationship: RelationshipView): string {
  return `Funktion ändern bei „${relationshipLine(relationship)}"`;
}

/**
 * Die Bestaetigung nach dem Aendern: die neue Funktion, die bisherige (fuer
 * den Rueckweg, CM-UX-004) und dass sie nicht verloren ist.
 */
export function roleChangedAnnouncement(relationship: RelationshipView, roleCode: string | null): string {
  const neu = roleLabel(roleCode);
  const bisher = roleLabel(relationship.roleCode);
  const wirkung = neu === undefined ? 'trägt jetzt keine Funktion mehr' : `trägt jetzt die Funktion „${neu}"`;
  const vorher = bisher === undefined ? '' : ` (bisher: ${bisher})`;
  return `„${relationshipLine(relationship)}" ${wirkung}${vorher}. Die bisherige Angabe bleibt in der Historie gespeichert.`;
}

/**
 * Die Dublette, wenn beim Festhalten eine Funktion gewaehlt war: der
 * naheliegende Fehlweg ist, die Funktion ueber ein zweites Festhalten
 * nachzutragen. Der Satz zeigt den Weg in die Zeile (NFR-UX-005).
 */
export const DUPLICATE_WITH_ROLE_MESSAGE: PlainMessage = Object.freeze({
  headline: DUPLICATE_MESSAGE.headline,
  explanation: DUPLICATE_MESSAGE.explanation,
  nextStep: 'Um die Funktion einzutragen oder zu ändern, wähle in ihrer Zeile „Funktion ändern".',
  retryable: false,
});

/** Der Hinweis an einer Organisation, an der noch niemand arbeitet, frei mitarbeitet oder sie leitet. */
export const NO_CONTACT_PERSONS_STATEMENT = 'Für diese Organisation sind noch keine Ansprechpartner:innen festgehalten.';

/** Die Funktion laesst sich an dieser Beziehung nicht (mehr) setzen -- die Art ist stillgelegt oder traegt keine. */
export const ROLE_NOT_SUPPORTED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Die Funktion ließ sich nicht ändern.',
  explanation: 'An dieser Art von Beziehung lässt sich keine Funktion mehr eintragen. Es wurde nichts geändert.',
  nextStep: 'Die Liste ist neu geladen. Beende die Beziehung bei Bedarf und halte sie mit der passenden Art neu fest.',
  retryable: false,
});

// Funktion aendern ohne Antwort. Kein "Erneut versuchen": die Wiederholung mit
// dem alten Stand liefe ins Leere, wenn der erste Versuch angekommen ist. Der
// Abschnitt laedt still nach; der Satz sagt, ob das gelang.
export const ROLE_UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Funktion geändert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Die Beziehungen sind neu geladen. Dort siehst du, was gilt.',
  retryable: false,
});

export const ROLE_UNREACHABLE_NOT_RELOADED_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Funktion geändert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an. Der neue Stand ließ sich gerade nicht laden.',
  nextStep: 'Lade die Seite neu. Dann siehst du, was gilt.',
  retryable: false,
});

/** Der Satz fuer einen Feldfehler des Servers, am Feld und in Worten. */
export function fieldMessageFor(fieldPath: string): string {
  if (fieldPath === 'relationshipTypeCode' || fieldPath === 'contactSide') {
    return 'Diese Art der Beziehung passt nicht zu diesem Kontakt. Die Auswahl ist neu geladen; wähle erneut.';
  }
  return 'Dieser Kontakt passt nicht zu der gewählten Art der Beziehung. Wähle einen anderen Treffer oder eine andere Art.';
}

// --- Die Aufrufe -------------------------------------------------------------

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return { status: response.status, answered: payload !== undefined, ...correlationOf(body) };
}

function reasonCodeOf(payload: unknown): string | undefined {
  const body = (payload ?? {}) as Record<string, unknown>;
  return typeof body.reasonCode === 'string' ? body.reasonCode : undefined;
}

function relationshipOf(value: unknown): RelationshipView | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.relationshipId !== 'string' ||
    typeof row.relationshipTypeCode !== 'string' ||
    typeof row.reading !== 'string' ||
    row.reading.trim().length === 0 ||
    typeof row.counterpartPartyId !== 'string' ||
    typeof row.counterpartDisplayName !== 'string' ||
    typeof row.validFrom !== 'string' ||
    typeof row.lockVersion !== 'number' ||
    !Number.isSafeInteger(row.lockVersion) ||
    row.lockVersion < 1 ||
    (row.roleCode !== undefined && row.roleCode !== null && typeof row.roleCode !== 'string')
  ) {
    return undefined;
  }
  return {
    relationshipId: row.relationshipId,
    relationshipTypeCode: row.relationshipTypeCode,
    reading: row.reading,
    counterpartPartyId: row.counterpartPartyId,
    counterpartDisplayName: row.counterpartDisplayName,
    validFrom: row.validFrom,
    lockVersion: row.lockVersion,
    roleCode: typeof row.roleCode === 'string' ? row.roleCode : null,
  };
}

function optionOf(value: unknown): RelationshipOption | undefined {
  const row = (value ?? {}) as Record<string, unknown>;
  if (
    typeof row.relationshipTypeCode !== 'string' ||
    (row.contactSide !== 'a' && row.contactSide !== 'b') ||
    typeof row.reading !== 'string' ||
    row.reading.trim().length === 0 ||
    (row.counterpartKind !== null && row.counterpartKind !== 'person' && row.counterpartKind !== 'organization')
  ) {
    // Fail-closed: eine Lesart, die den Vertrag bricht, wird nicht angeboten.
    return undefined;
  }
  return {
    relationshipTypeCode: row.relationshipTypeCode,
    contactSide: row.contactSide,
    reading: row.reading,
    counterpartKind: row.counterpartKind,
  };
}

function fieldProblemsOf(payload: unknown): readonly FieldProblem[] {
  const body = (payload ?? {}) as Record<string, unknown>;
  if (!Array.isArray(body.errors)) return [];
  return body.errors.flatMap((entry: unknown) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    return typeof row.fieldPath === 'string' && typeof row.reasonCode === 'string' ? [{ fieldPath: row.fieldPath, reasonCode: row.reasonCode }] : [];
  });
}

function relationshipsUrl(baseUrl: string, partyId: string, cursor?: string, relationshipTypeCode?: string): string {
  const url = `${baseUrl}/parties/${encodeURIComponent(partyId)}/relationships`;
  const query = [
    ...(cursor === undefined ? [] : [`cursor=${encodeURIComponent(cursor)}`]),
    ...(relationshipTypeCode === undefined ? [] : [`relationshipTypeCode=${encodeURIComponent(relationshipTypeCode)}`]),
  ];
  return query.length === 0 ? url : `${url}?${query.join('&')}`;
}

/**
 * Eine Seite der Kanten, ab `cursor` (aus `nextCursor` der vorigen). Die Route
 * liefert hoechstens 50 je Seite; die Seitengroesse ist eine technische Grenze,
 * und ueber den Cursor ist jede sichtbare Kante erreichbar. Ohne die Lesarten:
 * wer nur liest, braucht die Auswahl fuer eine neue Kante nicht.
 */
export async function fetchRelationshipPage(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly cursor?: string;
  /** Nur Kanten dieses Typs (#791), etwa `leads` fuer den Abgleich der Leitung. */
  readonly relationshipTypeCode?: string;
  readonly send?: SendLike;
}): Promise<RelationshipPageResult> {
  const send = options.send ?? fetch;
  const response = await send(relationshipsUrl(options.baseUrl, options.partyId, options.cursor, options.relationshipTypeCode), {
    headers: { authorization: `Bearer ${options.accessToken}` },
  });
  const payload: unknown = await response.json().catch(() => undefined);
  if (response.status === 404) {
    return { outcome: 'not-found' };
  }
  if (!response.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(response, payload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const relationships: RelationshipView[] = [];
  for (const row of Array.isArray(body.relationships) ? body.relationships : []) {
    const view = relationshipOf(row);
    if (view !== undefined) relationships.push(view);
  }
  const hasMore = body.hasMore === true;
  const nextCursor = hasMore && typeof body.nextCursor === 'string' && body.nextCursor !== '' ? body.nextCursor : undefined;
  return { outcome: 'loaded', relationships, hasMore, nextCursor };
}

/**
 * Liest Kanten UND Lesarten -- zwei Routen, weil beide in einer Antwort den
 * Knotenetat des Servers sprengten. Beide laufen gleichzeitig; der Abschnitt
 * sieht EIN Ergebnis wie bisher. Scheitert eine, gilt ihr Ausgang: ein halber
 * Abschnitt (Kanten ohne Auswahl) waere eine stille Luecke.
 */
export async function fetchRelationships(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly send?: SendLike;
}): Promise<RelationshipsResult> {
  const send = options.send ?? fetch;
  const headers = { authorization: `Bearer ${options.accessToken}` };
  const [kanten, lesarten] = await Promise.all([
    send(relationshipsUrl(options.baseUrl, options.partyId), { headers }),
    send(`${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/relationship-options`, { headers }),
  ]);
  const [payload, optionPayload] = await Promise.all([
    kanten.json().catch(() => undefined),
    lesarten.json().catch(() => undefined),
  ]);
  if (kanten.status === 404 || lesarten.status === 404) {
    return { outcome: 'not-found' };
  }
  if (!kanten.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(payload), factsOf(kanten, payload)) };
  }
  if (!lesarten.ok) {
    return { outcome: 'rejected', message: plainMessageFor(reasonCodeOf(optionPayload), factsOf(lesarten, optionPayload)) };
  }
  const body = (payload ?? {}) as Record<string, unknown>;
  const relationships: RelationshipView[] = [];
  for (const row of Array.isArray(body.relationships) ? body.relationships : []) {
    const view = relationshipOf(row);
    if (view !== undefined) relationships.push(view);
  }
  const optionBody = (optionPayload ?? {}) as Record<string, unknown>;
  const offered: RelationshipOption[] = [];
  for (const row of Array.isArray(optionBody.options) ? optionBody.options : []) {
    const option = optionOf(row);
    if (option !== undefined) offered.push(option);
  }
  const hasMore = body.hasMore === true;
  const nextCursor = hasMore && typeof body.nextCursor === 'string' && body.nextCursor !== '' ? body.nextCursor : undefined;
  return { outcome: 'loaded', relationships, hasMore, ...(nextCursor === undefined ? {} : { nextCursor }), options: offered };
}

/**
 * Haelt eine Beziehung fest. Der Rumpf traegt die Lesart als Typ plus Seite,
 * die Gegenpartei und die Absendekennung -- sonst nichts (ADR-0061 Ziffer 10:
 * Sichtbarkeit, Zeit und Kontext setzt LIAISON).
 *
 * Ein Abbruch auf Transportebene WIRFT (fetch wirft); der Bildschirm faengt
 * ihn und haelt die Kennung (client-request.ts).
 */
export async function createRelationship(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly option: Pick<RelationshipOption, 'relationshipTypeCode' | 'contactSide'>;
  readonly counterpartPartyId: string;
  /** Die Funktion, nur an works_for/contributes_to; ohne Wahl faellt das Feld weg. */
  readonly roleCode?: string | undefined;
  readonly clientRequestId: string;
  readonly send?: SendLike;
}): Promise<CreateRelationshipResult> {
  const send = options.send ?? fetch;
  const response = await send(relationshipsUrl(options.baseUrl, options.partyId), {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({
      relationshipTypeCode: options.option.relationshipTypeCode,
      contactSide: options.option.contactSide,
      counterpartPartyId: options.counterpartPartyId,
      ...(options.roleCode === undefined ? {} : { roleCode: options.roleCode }),
      clientRequestId: options.clientRequestId,
    }),
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 201) {
    const body = (payload ?? {}) as Record<string, unknown>;
    return { outcome: 'created', relationshipId: typeof body.relationshipId === 'string' ? body.relationshipId : '' };
  }
  const reasonCode = reasonCodeOf(payload);
  if (response.status === 404) {
    if (reasonCode === 'counterpart_not_found') {
      return { outcome: 'rejected', message: COUNTERPART_GONE_MESSAGE, reasonCode };
    }
    return { outcome: 'not-found' };
  }
  if (response.status === 422) {
    const errors = fieldProblemsOf(payload);
    // Die Kennung erzeugt der Browser; ein Feld zum Korrigieren gibt es
    // nicht. Dann der allgemeine Rueckfall statt einer Feldmeldung ohne Feld.
    if (errors.length === 0 || onlyRequestIdRejected(errors)) {
      return { outcome: 'rejected', message: CONFLICT_MESSAGE, reasonCode };
    }
    return { outcome: 'invalid', errors };
  }
  if (response.status === 409) {
    return { outcome: 'rejected', message: (reasonCode === undefined ? undefined : CREATE_CONFLICTS[reasonCode]) ?? CONFLICT_MESSAGE, reasonCode };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)), reasonCode };
}

export type ChangeRoleResult =
  | { readonly outcome: 'changed'; readonly relationshipId: string }
  | { readonly outcome: 'rejected'; readonly message: PlainMessage };

const ROLE_MESSAGES: CommandMessages = Object.freeze({
  notFound: RELATIONSHIP_GONE_MESSAGE,
  conflictFallback: FUNCTION_OUTDATED_MESSAGE,
  conflicts: ROLE_CONFLICTS,
});

/**
 * Aendert die Funktion: der Server beendet die Kante und legt dieselbe
 * Aussage mit der neuen Funktion an, in einer Transaktion (F2). Die Antwort
 * traegt eine NEUE relationshipId; der Abschnitt laedt danach neu. `null`
 * entfernt die Funktion. Den Stand schickt der Aufrufer aus der Zeile mit.
 */
export async function changeRelationshipRole(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly relationship: RelationshipView;
  readonly roleCode: string | null;
  readonly send?: SendLike;
}): Promise<ChangeRoleResult> {
  const ergebnis = await sendCommand({
    url: `${relationshipsUrl(options.baseUrl, options.partyId)}/${encodeURIComponent(options.relationship.relationshipId)}/role`,
    accessToken: options.accessToken,
    body: { lockVersion: options.relationship.lockVersion, roleCode: options.roleCode },
    messages: ROLE_MESSAGES,
    applied: (record) => ({ relationshipId: typeof record.relationshipId === 'string' ? record.relationshipId : '' }),
    ...(options.send === undefined ? {} : { send: options.send }),
  });
  if (ergebnis.outcome === 'applied') {
    return { outcome: 'changed', relationshipId: ergebnis.relationshipId };
  }
  if (ergebnis.outcome === 'invalid') {
    // Eine Feldablehnung an der Funktion heisst: diese Art traegt keine (mehr);
    // alles andere ist ein unbrauchbarer Stand -- neu laden hilft.
    const anFunktion = ergebnis.errors.some((fehler) => fehler.fieldPath === 'roleCode');
    return { outcome: 'rejected', message: anFunktion ? ROLE_NOT_SUPPORTED_MESSAGE : FUNCTION_OUTDATED_MESSAGE };
  }
  return { outcome: 'rejected', message: ergebnis.message };
}

/** Beendet eine Beziehung. Nichts wird geloescht; das Ende ist ein Datum (ADR-0061 Ziffer 9). */
export async function endRelationship(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly relationship: RelationshipView;
  readonly send?: SendLike;
}): Promise<EndRelationshipResult> {
  const send = options.send ?? fetch;
  const url = `${relationshipsUrl(options.baseUrl, options.partyId)}/${encodeURIComponent(options.relationship.relationshipId)}/end`;
  const response = await send(url, {
    method: 'POST',
    headers: { authorization: `Bearer ${options.accessToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ lockVersion: options.relationship.lockVersion }),
  });
  const payload = await response.json().catch(() => undefined);
  if (response.status === 200) {
    return { outcome: 'ended' };
  }
  const reasonCode = reasonCodeOf(payload);
  if (response.status === 404) {
    if (reasonCode === 'relationship_not_found') {
      return { outcome: 'rejected', message: RELATIONSHIP_GONE_MESSAGE, reasonCode };
    }
    return { outcome: 'not-found' };
  }
  if (response.status === 409) {
    return { outcome: 'rejected', message: (reasonCode === undefined ? undefined : END_CONFLICTS[reasonCode]) ?? OUTDATED_MESSAGE, reasonCode };
  }
  if (response.status === 422) {
    // Der Stand der Zeile war unbrauchbar -- neu laden hilft, wiederholen nicht.
    return { outcome: 'rejected', message: OUTDATED_MESSAGE, reasonCode };
  }
  return { outcome: 'rejected', message: plainMessageFor(reasonCode, factsOf(response, payload)), reasonCode };
}
